import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getAllCourseCategories } from "../../../core/data/redux/courseCategoriesSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  createOrUpdateNocOnlineCourse,
  getNocOnlineCourse,
  getCourseDepartments,
} from "../../../core/data/redux/nocOnlineCoursesSlice";
import { all_routes } from "../../router/all_routes";

interface NOCOnlineCourseCreateProps { }

interface CourseDocument {
  id: string;
  name: string;
  file: File | null;
  awsKey: string | null;
  uploadedAt: string | null;
  uploading: boolean;
}

interface CourseDetails {
  id: number;
  category_id: string;
  department: string;
  course_id: string;
  course: string;
  plateform: string;
  duration: string;
  course_fee: string;
  currency: string;
  contributor: string;
  sapCreatedAt: string | null;
  sapModifiedAt: string | null;
  created_at: string;
  modified_at: string;
  deleted_at: string | null;
}

const NOCOnlineCourseCreate = (props: NOCOnlineCourseCreateProps) => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { id } = useParams<{ id: string }>(); // Get ID from URL for edit mode

  const employeesState = useSelector((state: any) => state.employeesState);
  const courseCategoriesState = useSelector(
    (state: any) => state.courseCategoriesState
  );
  const nocOnlineCoursesState = useSelector(
    (state: any) => state.nocOnlineCoursesState
  );
  const user = employeesState?.data?.data;
  const courseCategories = courseCategoriesState?.data?.data;
  const courseDepartments = nocOnlineCoursesState?.courseDepartments;
  const isEditMode = !!id; // Check if we're in edit mode

  // Get unique course departments
  const uniqueCourseDepartments = useMemo(() => {
    if (!courseDepartments || courseDepartments.length === 0) return [];

    const uniqueDepts = courseDepartments.reduce((acc: any[], current: any) => {
      const isDuplicate = acc.some(
        (dept: any) => dept.department === current.department
      );
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    return uniqueDepts;
  }, [courseDepartments]);

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState<"success" | "error">("success");
  const [formData, setFormData] = useState({
    // Employee Details
    name: "",
    designation: "",
    dateOfJoining: "",
    employmentStatus: "Direct recruit",
    department: "",
    contactNumber: "",
    email: "",
    dateOfBirth: "",
    // Course Details
    natureOfDutyPerfo: "",
    onlineCourseCategory: "",
    courseDepartment: "",
    courseBrochure: "",
    updatedCourseFee: "",
    lastDateOfRegistration: "",
    courseName: "",
    keyForCourse: "",
    selectedCourseDetails: null as CourseDetails | null,
    dateOfCourseJoining: "",
    anyRemarks: "",
    declarationAccepted: false,
  });

  // Helper function to check if selected category is Generic
  const isGenericCategory = () => {
    const selectedCategory = courseCategories?.find(
      (cat: any) => cat.category_id === formData.onlineCourseCategory
    );
    const categoryTitle = selectedCategory?.category_title
      ?.trim()
      .toLowerCase();
    return categoryTitle === "g" || categoryTitle === "generic courses" || categoryTitle?.includes("generic");
  };

  // Helper function to check if selected category is Specialized
  const isSpecializedCategory = () => {
    const selectedCategory = courseCategories?.find(
      (cat: any) => cat.category_id === formData.onlineCourseCategory
    );
    const categoryTitle = selectedCategory?.category_title
      ?.trim()
      .toLowerCase();
    return (
      categoryTitle === "specialized courses" ||
      categoryTitle?.includes("specialized")
    );
  };

  // Get filtered course departments based on selected category
  const filteredCourseDepartments = useMemo(() => {
    if (isSpecializedCategory()) {
      // Remove "ALL" option for Specialized Courses
      return uniqueCourseDepartments?.filter(
        (dept: any) => dept.department?.trim().toUpperCase() !== "ALL"
      );
    }
    return uniqueCourseDepartments;
  }, [uniqueCourseDepartments, formData.onlineCourseCategory]);

  // Auto-select course department if only 1 department is available (e.g. "All" for Generic Courses)
  useEffect(() => {
    if (filteredCourseDepartments && filteredCourseDepartments.length === 1) {
      const singleDept = filteredCourseDepartments[0].department;
      if (formData.courseDepartment !== singleDept) {
        setFormData((prev) => ({
          ...prev,
          courseDepartment: singleDept,
        }));
      }
    }
  }, [filteredCourseDepartments, formData.courseDepartment]);

  // Get courses for selected department
  const coursesForSelectedDepartment = useMemo(() => {
    if (!formData.courseDepartment || !courseDepartments || !Array.isArray(courseDepartments)) return [];

    // Filter courses by selected department
    const departmentCourses = courseDepartments.filter(
      (item: any) =>
        item.department?.trim().toLowerCase() === formData.courseDepartment?.trim().toLowerCase()
    );

    // Get unique courses
    const uniqueCourses = departmentCourses.reduce(
      (acc: any[], current: any) => {
        const isDuplicate = acc.some(
          (course: any) => String(course.course_id) === String(current.course_id)
        );
        if (!isDuplicate && (current.course || current.course_id)) {
          acc.push(current);
        }
        return acc;
      },
      []
    );

    return uniqueCourses;
  }, [courseDepartments, formData.courseDepartment]);

  const [courseDocuments, setCourseDocuments] = useState<CourseDocument[]>([
    {
      id: "syllabus",
      name: "Course Syllabus/Curriculum",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
    {
      id: "brochure",
      name: "Course Brochure",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
    {
      id: "approval_letter",
      name: "Supervisor Approval Letter",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
    {
      id: "payment_receipt",
      name: "Payment Receipt/Invoice",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
    {
      id: "certificate",
      name: "Previous Certificate",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
    {
      id: "others",
      name: "Other Course Documents",
      file: null,
      awsKey: null,
      uploadedAt: null,
      uploading: false,
    },
  ]);
  const [showOverviewModal, setShowOverviewModal] = useState(!id); // Show only in create mode, not edit

  // Load employee profile on component mount
  useEffect(() => {
    dispatch(getMyProfile({}));

    // Set a timeout to stop loading if API takes too long
    const timeout = setTimeout(() => {
      setIsLoadingProfile(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllCourseCategories({}));
  }, [dispatch]);

  // Update form data when user data is available
  useEffect(() => {
    if (user?.details) {
      const userDetails = user.details;

      setFormData((prev) => ({
        ...prev,
        name: `${userDetails.firstname || ""} ${userDetails.lsastname || userDetails.lastname || ""
          }`.trim(),
        designation:
          userDetails.sap_department_designation_wings?.designation_name ||
          userDetails.designation_details?.designationName ||
          `Designation ID: ${userDetails.designationID || ""}`,
        dateOfJoining: userDetails.date_of_joining
          ? moment(userDetails.date_of_joining).format("DD-MM-YYYY")
          : "",
        employmentStatus:
          userDetails.employmentType || user.employmentType || user.status,
        department:
          userDetails.sap_department_designation_wings?.department_name ||
          userDetails.department_details?.departmentName ||
          `Department ID: ${userDetails.department || ""}`,
        contactNumber: userDetails.phone || "",
        email: userDetails.email || "",
        dateOfBirth: userDetails.birthdate
          ? moment(userDetails.birthdate).format("DD-MM-YYYY")
          : "",
      }));

      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "fulfilled" && !user?.details) {
      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "rejected") {
      // API failed, stop loading and show error
      console.error("Failed to load profile:", employeesState?.error);
      setIsLoadingProfile(false);
    }
  }, [user, employeesState?.loading, employeesState?.error]);

  // Load existing data in edit mode
  useEffect(() => {
    const loadExistingData = async () => {
      if (!isEditMode || !id) return;

      try {
        setIsLoadingProfile(true);
        const response = await dispatch(
          getNocOnlineCourse({ id: parseInt(id) })
        ).unwrap();
        const data = response?.data || response;

        // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
        const convertDateFromBackend = (dateStr: string) => {
          if (!dateStr) return "";
          const parts = dateStr.split("-");
          if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
          }
          return dateStr;
        };

        if (data) {
          console.log('Edit Mode - Loaded Data:', data);

          // Map nature of duty from backend response
          // Backend returns a single letter code: "R" = Regular, "S" = Shift
          let natureOfDuty = "";
          if (data.nature_of_duty?.nature_of_duty_title) {
            // If the backend joined the nature_of_duty table, use the title
            natureOfDuty = data.nature_of_duty.nature_of_duty_title;
          } else if (data.natureOfDutyPerfo) {
            // Map the code to the text value
            const dutyMap: Record<string, string> = {
              "R": "Regular",
              "S": "Shift",
              "r": "Regular",
              "s": "Shift",
              "01": "Regular",
              "02": "Shift",
              "1": "Regular",
              "2": "Shift"
            };
            natureOfDuty = dutyMap[data.natureOfDutyPerfo] || data.natureOfDutyPerfo;
          }

          // Populate form data
          setFormData((prev) => ({
            ...prev,
            natureOfDutyPerfo: natureOfDuty,
            onlineCourseCategory: data.onlineCourseCategory || "",
            courseDepartment: data.onlineCourseDepartment || "",
            courseName: data.onlineCourseName || "",
            keyForCourse: data.keyForCourse || "",
            courseBrochure: data.courseBrochure || "",
            updatedCourseFee: data.courseFee || "",
            lastDateOfRegistration: convertDateFromBackend(data.lastDateOfRegistration) || "",
            dateOfCourseJoining: convertDateFromBackend(data.dateOfCourseJoining) || "",
            anyRemarks: data.remarks || "",
            declarationAccepted: false, // Reset declaration for edit
          }));

          if (data.onlineCourseCategory) {
            dispatch(getCourseDepartments({ category_id: data.onlineCourseCategory }));
          }

          // Populate documents if available
          if (data.documents && Array.isArray(data.documents) && data.documents.length > 0) {
            const existingDocs: CourseDocument[] = data.documents.map(
              (doc: any, index: number) => {
                // Determine document type based on available data
                const docName = doc.fileName || doc.typeOfAttachedDoc || `Document ${index + 1}`;

                return {
                  id: `existing-${index}`,
                  name: docName,
                  file: null,
                  awsKey: doc.serverKey || doc.fileName || null,
                  uploadedAt: doc.created_at || new Date().toISOString(),
                  uploading: false,
                };
              }
            );
            setCourseDocuments(existingDocs);
          }

          // toast.success("Data loaded successfully"); // Removed toast message for better UX
        }
      } catch (error: any) {
        console.error("Failed to load existing data:", error);
        toast.error("Failed to load existing data");
        navigate(routes.noc.onlineCourse);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadExistingData();
  }, [isEditMode, id, dispatch, navigate, routes.noc.onlineCourse]);

  // Find and set selected course details once courseDepartments load
  useEffect(() => {
    if (courseDepartments && courseDepartments.length > 0) {
      if (formData.keyForCourse || formData.courseName) {
        const selectedCourse = courseDepartments.find(
          (course: any) =>
            (formData.keyForCourse && course.course_id === formData.keyForCourse) ||
            course.course === formData.courseName ||
            course.course_id === formData.courseName
        );
        if (selectedCourse && !formData.selectedCourseDetails) {
          setFormData((prev) => ({
            ...prev,
            courseName: selectedCourse.course,
            keyForCourse: selectedCourse.course_id,
            selectedCourseDetails: selectedCourse,
          }));
        }
      }
    }
  }, [courseDepartments, formData.keyForCourse, formData.courseName, formData.selectedCourseDetails]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // If Category of Online Course is changed, dispatch getCourseDepartments with category_id
    if (name === "onlineCourseCategory") {
      if (value) {
        dispatch(getCourseDepartments({ category_id: value }));
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        courseDepartment: "",
        courseName: "",
        keyForCourse: "",
        selectedCourseDetails: null,
      }));
      return;
    }

    if (name === "courseDepartment") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        courseName: "",
        keyForCourse: "",
        selectedCourseDetails: null,
      }));
      return;
    }

    // If Course Name is changed, update the selected course details and keyForCourse
    if (name === "courseName") {
      const selectedCourse = courseDepartments?.find(
        (course: any) => String(course.course_id) === String(value)
      );

      setFormData((prev) => ({
        ...prev,
        courseName: selectedCourse ? selectedCourse.course : "",
        keyForCourse: value,
        selectedCourseDetails: selectedCourse || null,
        updatedCourseFee: selectedCourse ? (selectedCourse.course_fee || "") : prev.updatedCourseFee,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle date changes
  const handleDateChange = (name: string, date: any, dateString: string) => {
    setFormData((prev) => ({ ...prev, [name]: dateString }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.declarationAccepted) {
      toast.error("Please accept the declaration before submitting");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(
      isEditMode ? "Updating NOC request..." : "Submitting NOC request..."
    );

    try {
      // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
      const convertDateFormat = (dateStr: string) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
        }
        return dateStr;
      };

      // Prepare payload for submission matching mobile app schema
      const payload: any = {
        is_draft: false,
        natureOfDutyPerfo: formData.natureOfDutyPerfo
          ? formData.natureOfDutyPerfo.trim().charAt(0).toUpperCase()
          : "",
        courseFee: formData.updatedCourseFee,
        currency: formData.selectedCourseDetails?.currency || "INR",
        courseBrochure: formData.courseBrochure,
        onlineCourseCategory: formData.onlineCourseCategory,
        onlineCourseName: formData.courseName,
        onlineCourseDepartment: formData.courseDepartment,
        keyForCourse:
          formData.keyForCourse ||
          formData.selectedCourseDetails?.course_id ||
          "",
        dateOfCourseJoining:
          convertDateFormat(formData.dateOfCourseJoining) || null,
        lastDateOfRegistration:
          convertDateFormat(formData.lastDateOfRegistration) || null,
        remarks: formData.anyRemarks,
        hRemarks: "",
        department: formData.department,
        contactNumber: formData.contactNumber,
        email: formData.email,
      };

      // Add ID only if in edit mode
      if (isEditMode && id) {
        payload.id = parseInt(id);
      }

      await dispatch(createOrUpdateNocOnlineCourse(payload)).unwrap();

      const successMessage = isEditMode
        ? "NOC application updated successfully!"
        : "NOC application submitted successfully!";

      setResponseMessage(successMessage);
      setResponseType("success");

      // Keep loader visible for 10 more seconds after success
      setTimeout(() => {
        setShowLoadingModal(false);
        setShowResponseModal(true);
      }, 10000); // 10 second delay
    } catch (error: any) {
      console.error("Failed to submit NOC request:", error);
      const errorMessage =
        error?.message ||
        error?.error ||
        error ||
        "Failed to submit application";

      setResponseMessage(errorMessage);
      setResponseType("error");
      setShowLoadingModal(false);
      setShowResponseModal(true);
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    // Validate required fields for draft
    if (!formData.courseName || !formData.courseName.trim()) {
      toast.error("Online Course Name is required to save as draft");
      return;
    }

    if (!formData.updatedCourseFee || !formData.updatedCourseFee.trim()) {
      toast.error("Course Fee is required to save as draft");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(isEditMode ? "Updating draft..." : "Saving draft...");

    try {
      // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
      const convertDateFormat = (dateStr: string) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
        }
        return dateStr;
      };

      // Prepare payload for draft matching mobile app schema
      const payload: any = {
        is_draft: true,
        natureOfDutyPerfo: formData.natureOfDutyPerfo
          ? formData.natureOfDutyPerfo.trim().charAt(0).toUpperCase()
          : "",
        courseFee: formData.updatedCourseFee || "",
        currency: formData.selectedCourseDetails?.currency || "INR",
        courseBrochure: formData.courseBrochure || "",
        onlineCourseCategory: formData.onlineCourseCategory || "",
        onlineCourseName: formData.courseName || "",
        onlineCourseDepartment: formData.courseDepartment || "",
        keyForCourse:
          formData.keyForCourse ||
          formData.selectedCourseDetails?.course_id ||
          "",
        dateOfCourseJoining:
          convertDateFormat(formData.dateOfCourseJoining) || null,
        lastDateOfRegistration:
          convertDateFormat(formData.lastDateOfRegistration) || null,
        remarks: formData.anyRemarks || "",
        hRemarks: "",
        department: formData.department || "",
        contactNumber: formData.contactNumber || "",
        email: formData.email || "",
      };

      // Add ID only if in edit mode
      if (isEditMode && id) {
        payload.id = parseInt(id);
      }

      await dispatch(createOrUpdateNocOnlineCourse(payload)).unwrap();
      toast.success(
        isEditMode ? "Draft updated successfully!" : "Draft saved successfully!"
      );

      // Navigate back to list page after showing toast
      setTimeout(() => {
        navigate(routes.noc.onlineCourse);
      }, 1500);
    } catch (error: any) {
      console.error("Failed to save draft:", error);
      const errorMessage =
        error?.message || error?.error || error || "Failed to save draft";
      toast.error(errorMessage);
    } finally {
      setShowLoadingModal(false);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <div className="d-flex align-items-center">
                <Link
                  to="#"
                  className="text-primary text-decoration-none me-3"
                  onClick={() => navigate(-1)}
                >
                  <i className="ti ti-arrow-left fs-24"></i>
                </Link>
                <h3 className="page-title mb-0">
                  {isEditMode
                    ? "Edit NOC for Online Course"
                    : "Create NOC for Online Course"}
                </h3>
              </div>
              <nav className="mt-2">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.noc.main}>NOC Services</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.noc.onlineCourse}>Online Course</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit Request" : "Create Request"}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {isLoadingProfile ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "300px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading your profile data...</span>
            </div>
          ) : employeesState?.loading === "rejected" ? (
            <div className="alert alert-warning d-flex align-items-center mb-3">
              <i className="ti ti-alert-triangle me-2"></i>
              <div>
                <strong>Unable to load profile data.</strong>
                <p className="mb-0">
                  There was an error loading your profile. Error:{" "}
                  {employeesState?.error || "Unknown error"}
                </p>
                <p className="mb-0 mt-2">
                  Please contact support or try again later.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Employee Details Section */}
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Employee Details</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {/* Name */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              value={formData.name}
                              style={{ backgroundColor: "#f8f9fa" }}
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Designation */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Designation
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="designation"
                              value={formData.designation}
                              style={{ backgroundColor: "#f8f9fa" }}
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Date of Joining in DMRC */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Date of Joining in DMRC
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="dateOfJoining"
                              value={formData.dateOfJoining}
                              style={{ backgroundColor: "#f8f9fa" }}
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Employment Status */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Employment Status
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="employmentStatus"
                              value={formData.employmentStatus}
                              style={{ backgroundColor: "#f8f9fa" }}
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Department */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Department <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="department"
                              value={formData.department}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Contact Number */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Contact Number{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              name="contactNumber"
                              value={formData.contactNumber}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Email ID */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Email ID <span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Date of Birth */}
                        {/* <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium"
                            >
                              Date of Birth
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="dateOfBirth"
                              value={formData.dateOfBirth}
                              style={{ backgroundColor: "#f8f9fa" }}
                              readOnly
                            />
                          </div>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Application Details Section */}
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h4 className="card-title mb-0">
                        <i className="ti ti-clipboard me-2"></i>
                        Application Details
                      </h4>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Nature of Duty Performing{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="natureOfDutyPerfo"
                              value={formData.natureOfDutyPerfo}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Nature of Duty</option>
                              {[{ key: "G", label: "General" }, { key: "S", label: "Shift" }].map((option) => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Category of Online Course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="onlineCourseCategory"
                              value={formData.onlineCourseCategory}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Category</option>
                              {courseCategories?.map((category: any) => (
                                <option
                                  key={category.category_id}
                                  value={category.category_id}
                                >
                                  {category.category_title}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Course Department{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="courseDepartment"
                              value={formData.courseDepartment}
                              onChange={handleInputChange}
                              required
                              disabled={!formData.onlineCourseCategory || filteredCourseDepartments.length === 0}
                            >
                              <option value="">Select Course Department</option>
                              {filteredCourseDepartments?.map(
                                (dept: any, index: number) => (
                                  <option
                                    key={`${dept.department}-${index}`}
                                    value={dept.department}
                                  >
                                    {dept.department}
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        </div>

                        {/* Course Name - Shows when Course Department is selected */}
                        {formData.courseDepartment && (
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Course Name{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                className="form-select"
                                name="courseName"
                                value={formData.keyForCourse || ""}
                                onChange={handleInputChange}
                                required
                              >
                                <option value="">Select Course Name</option>
                                {coursesForSelectedDepartment?.map(
                                  (course: any) => (
                                    <option
                                      key={course.course_id}
                                      value={course.course_id}
                                    >
                                      {course.course}
                                    </option>
                                  )
                                )}
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Course Details Table - Shows when Course Name is selected */}
                        {formData.selectedCourseDetails && (
                          <div className="col-md-12">
                            <div
                              className="card"
                              style={{
                                backgroundColor: "#fffbea",
                                border: "1px solid #f0e68c",
                              }}
                            >
                              <div
                                className="card-header"
                                style={{ backgroundColor: "#f0e68c" }}
                              >
                                <h6 className="mb-0 fw-bold text-dark">
                                  Course details as per HR Compendium
                                </h6>
                              </div>
                              <div className="card-body">
                                <div className="table-responsive">
                                  <table className="table table-bordered mb-0">
                                    <thead
                                      style={{ backgroundColor: "#f0e68c" }}
                                    >
                                      <tr>
                                        <th className="text-dark">Category</th>
                                        <th className="text-dark">
                                          Department
                                        </th>
                                        <th className="text-dark">
                                          Online Course Name
                                        </th>
                                        <th className="text-dark">Platform</th>
                                        <th className="text-dark">
                                          Duration of course
                                        </th>
                                        <th className="text-dark">
                                          Online course fee
                                        </th>
                                        <th className="text-dark">
                                          Contributors
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .category_id
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .department
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .course
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .plateform
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .duration
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .course_fee
                                          }{" "}
                                          {
                                            formData.selectedCourseDetails
                                              .currency
                                          }
                                        </td>
                                        <td>
                                          {
                                            formData.selectedCourseDetails
                                              .contributor
                                          }
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        <div className="col-md-12">
                          <div
                            className="alert alert-info mb-3"
                            style={{
                              backgroundColor: "#e8f5e9",
                              borderColor: "#c8e6c9",
                            }}
                          >
                            <h6 className="mb-3" style={{ color: "#2e7d32" }}>
                              <i className="ti ti-info-circle me-2"></i>
                              Instructions
                            </h6>
                            <ul
                              className="mb-0 text-dark"
                              style={{ paddingLeft: "20px", fontSize: "14px" }}
                            >
                              <li className="mb-2 text-dark">
                                For field "Web URL" please provide link to
                                programme brochure/website URL, having details
                                of fee, last date of registration etc.
                              </li>
                              <li className="mb-2 text-dark">
                                Updated course fee is to be filled if course fee
                                is currently different from the approved amount
                                in the HR Compendium.
                              </li>
                              <li className="mb-2 text-dark">
                                If fee is in dollars/other currency, please
                                provide amount in INR.
                              </li>
                              <li className="mb-0 text-dark">
                                If there is no fixed Last Registration Date then
                                please enter today's date.
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* Web URL */}
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Web URL (Programme Brochure/Website){" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="url"
                              className="form-control"
                              name="courseBrochure"
                              value={formData.courseBrochure}
                              onChange={handleInputChange}
                              required
                              placeholder="https://example.com/course-brochure"
                            />
                          </div>
                        </div>

                        {/* Updated Course Fee */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Updated Course Fee (in INR){" "}
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              name="updatedCourseFee"
                              value={formData.updatedCourseFee}
                              onChange={handleInputChange}
                              placeholder="Enter amount in INR"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>

                        {/* Last Date of Registration */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Last Date of Registration of Course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider>
                              <DatePicker
                                id="lastDateOfRegistration"
                                className="form-control"
                                format="DD-MM-YYYY"
                                value={
                                  formData.lastDateOfRegistration
                                    ? dayjs(
                                      formData.lastDateOfRegistration,
                                      "DD-MM-YYYY"
                                    )
                                    : null
                                }
                                onChange={(date, dateString) =>
                                  handleDateChange(
                                    "lastDateOfRegistration",
                                    date,
                                    dateString as string
                                  )
                                }
                              />
                            </ConfigProvider>
                          </div>
                        </div>

                        {/* Probable Date of Commencement */}
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Probable Date of Commencement of Course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider>
                              <DatePicker
                                id="dateOfCourseJoining"
                                className="form-control"
                                format="DD-MM-YYYY"
                                value={
                                  formData.dateOfCourseJoining
                                    ? dayjs(
                                      formData.dateOfCourseJoining,
                                      "DD-MM-YYYY"
                                    )
                                    : null
                                }
                                onChange={(date, dateString) =>
                                  handleDateChange(
                                    "dateOfCourseJoining",
                                    date,
                                    dateString as string
                                  )
                                }
                                disabledDate={(current) => {
                                  // Disable dates before Last Date of Registration
                                  if (!formData.lastDateOfRegistration)
                                    return false;
                                  const registrationDate = dayjs(
                                    formData.lastDateOfRegistration,
                                    "DD-MM-YYYY"
                                  );
                                  return (
                                    current &&
                                    current < registrationDate.startOf("day")
                                  );
                                }}
                              />
                            </ConfigProvider>
                          </div>
                        </div>

                        {/* Previous Reimbursements Table */}
                        <div className="col-md-12">
                          <div
                            className="card"
                            style={{
                              backgroundColor: "#e8f4f8",
                              border: "1px solid #b8dce8",
                            }}
                          >
                            <div
                              className="card-header"
                              style={{ backgroundColor: "#d0e8f2" }}
                            >
                              <h6 className="mb-0 fw-bold text-dark">
                                Previous Reimbursements for online courses
                              </h6>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-bordered mb-0">
                                  <thead style={{ backgroundColor: "#d0e8f2" }}>
                                    <tr>
                                      <th className="text-dark">Category</th>
                                      <th className="text-dark">Platform</th>
                                      <th className="text-dark">
                                        Duration of course
                                      </th>
                                      <th className="text-dark">Course Name</th>
                                      <th className="text-dark">
                                        Date of joining course
                                      </th>
                                      <th className="text-dark">
                                        Date of Completion
                                      </th>
                                      <th className="text-dark">
                                        Online Course Fee
                                      </th>
                                      <th className="text-dark">
                                        Date of Reimbursement
                                      </th>
                                      <th className="text-dark">
                                        End of Block
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td
                                        colSpan={9}
                                        className="text-center text-muted"
                                      >
                                        <i className="ti ti-info-circle me-2"></i>
                                        No Reimbursement taken for Online course
                                        previously
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Any Remarks */}
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Any remarks
                            </label>
                            <textarea
                              className="form-control"
                              name="anyRemarks"
                              value={formData.anyRemarks}
                              onChange={handleInputChange}
                              rows={4}
                              placeholder="Enter any additional remarks or comments"
                            />
                          </div>
                        </div>

                        {/* Terms & Conditions */}
                        <div className="col-md-12">
                          <div
                            className="card"
                            style={{
                              backgroundColor: "#fffbea",
                              border: "1px solid #f0e68c",
                            }}
                          >
                            <div
                              className="card-header"
                              style={{ backgroundColor: "#f0e68c" }}
                            >
                              <h6 className="mb-0 fw-bold text-dark">
                                Terms & Conditions:
                              </h6>
                            </div>
                            <div className="card-body">
                              <ol
                                className="mb-0 text-dark"
                                style={{
                                  paddingLeft: "20px",
                                  lineHeight: "1.8",
                                }}
                              >
                                <li className="mb-2">
                                  Claim will be reimbursed after qualifying of
                                  the course and submission of copy of
                                  Certificate awarded, payment details.
                                </li>
                                <li className="mb-2">
                                  Provision for accessories, such as computer &
                                  peripherals, laptops, internet connections
                                  etc., would not be provided by DMRC and
                                  employee undergoing the courses, shall have to
                                  arrange the same.
                                </li>
                                <li className="mb-2">
                                  The online Courses Should not be pursued
                                  during office/duty hours.
                                </li>
                                <li className="mb-2">
                                  Any kind of special
                                  consideration/leave/transfer shifting of duty
                                  etc. to pursue the Online Course, shall not be
                                  entertained.
                                </li>
                                <li className="mb-2">
                                  No further incentive/s shall be permissible on
                                  successful completion of the Online courses.
                                </li>
                                <li className="mb-2">
                                  The courses as contained in the approved list
                                  of courses shall only be pursued for
                                  reimbursement of course fee and addition of
                                  qualification in service records. Request(s)
                                  for consideration of any other course will not
                                  be considered for the above purposes.
                                </li>
                                <li className="mb-2">
                                  Employees are advised to avail discount (if
                                  offered), by the University/College.
                                </li>
                                <li className="mb-0">
                                  An employee can pursue any number of courses,
                                  however, fee reimbursement shall be restricted
                                  to Rs. 18000/- per block of 3 years(from
                                  completion of one course).
                                </li>
                              </ol>
                            </div>
                          </div>
                        </div>

                        {/* Declaration */}
                        <div className="col-md-12">
                          <div
                            className="card"
                            style={{
                              backgroundColor: "#d4edda",
                              border: "1px solid #a3cfbb",
                            }}
                          >
                            <div
                              className="card-header"
                              style={{ backgroundColor: "#c3e6cb" }}
                            >
                              <h6 className="mb-0 fw-bold text-dark">
                                Declaration
                              </h6>
                            </div>
                            <div className="card-body">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="declarationCheckbox"
                                  name="declarationAccepted"
                                  checked={formData.declarationAccepted}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      declarationAccepted: e.target.checked,
                                    }));
                                  }}
                                  required
                                />
                                <label
                                  className="form-check-label text-dark"
                                  htmlFor="declarationCheckbox"
                                  style={{ lineHeight: "1.6" }}
                                >
                                  I hereby declare that the information given
                                  above is true and my duty hours in DMRC would
                                  not get disturbed in any case. I accept the
                                  terms & conditions above. I may kindly be
                                  granted permission to enroll for Online
                                  Course.
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={() => navigate(routes.noc.onlineCourse)}
                  disabled={isSubmitting}
                >
                  <i className="ti ti-arrow-left me-1"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent any form validation
                    handleSaveDraft();
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy me-1"></i>
                      Save as Draft
                    </>
                  )}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-send me-1"></i>
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      {/* Overview Modal */}
      {showOverviewModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 1040 }}
          />

          {/* Overview Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
            style={{
              zIndex: 1050,
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {/* Modal Header */}
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                CHECKLIST FOR APPROVAL OF NOC FOR ONLINE COURSES
              </h5>
              <button
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={() => setShowOverviewModal(false)}
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4" style={{ lineHeight: "1.6" }}>
              <div className="text-dark">
                <p className="mb-3">
                  <strong>1.</strong> Must have at least two years of job
                  (Regular Basis/ Contract Basis) to do online course.
                </p>
                <p className="mb-3">
                  <strong>1.</strong> ऑनलाइन कोर्स करने के लिए कम से कम दो वर्ष
                  की नौकरी (नियमित/संविदात्मक आधार पर) होनी चाहिए।
                </p>

                <p className="mb-3">
                  <strong>2.</strong> Courses have been identified from approved
                  courses list of DMRA.
                </p>
                <p className="mb-3">
                  <strong>2.</strong> डीएमआरए की अनुमोदित पाठ्यक्रम सूची से
                  पाठ्यक्रमों की पहचान की गई है।
                </p>

                <p className="mb-3">
                  <strong>3.</strong> Generic course can be done by employees of
                  any department.
                </p>
                <p className="mb-3">
                  <strong>3.</strong> किसी भी विभाग का कर्मचारी जेनेरिक कोर्स कर
                  सकता है।
                </p>

                <p className="mb-3">
                  <strong>4.</strong> (A2) Specialized course can be done by
                  employees of respective discipline only.
                </p>
                <p className="mb-3">
                  <strong>4.</strong> केवल संबंधित विभागों के कर्मचारी ही
                  विशिष्ट पाठ्यक्रम (A2) कर सकते हैं।
                </p>

                <p className="mb-3">
                  <strong>5.</strong> Total course fee or single course fee in a
                  block of 3 year should not exceed Rs 18000.
                </p>
                <p className="mb-3">
                  <strong>5.</strong> तीन वर्ष के ब्लॉक में कुल पाठ्यक्रम शुल्क
                  या एकल पाठ्यक्रम शुल्क 18000 रु. से अधिक नहीं होना चाहिए।
                </p>

                <p className="mb-3">
                  <strong>6.</strong> The duration of the course should be
                  minimum 3 weeks and maximum 12 weeks.
                </p>
                <p className="mb-3">
                  <strong>6.</strong> कोर्स की अवधि न्यूनतम 3 सप्ताह और अधिकतम
                  12 सप्ताह होनी चाहिए।
                </p>

                <p className="mb-3">
                  <strong>7.</strong> Employees of Rolling Stock & Electrical
                  department can do course from list of either department.
                </p>
                <p className="mb-3">
                  <strong>7.</strong> रोल स्टॉक और विद्युत विभाग के कर्मचारी
                  दोनों में से किसी भी विभाग की सूची वाले कोर्स कर सकते हैं।
                </p>

                <p className="mb-3">
                  <strong>8.</strong> Each employee's block year, which will
                  begin from the date of first course by the employee.
                </p>
                <p className="mb-3">
                  <strong>8.</strong> प्रत्येक कर्मचारी का ब्लॉक वर्ष, कर्मचारी
                  द्वारा प्रथम कोर्स की तिथि से शुरू होगा।
                </p>

                <p className="mb-3">
                  <strong>9.</strong> Prior approval from the competent
                  authority is required for online course admission.
                </p>
                <p className="mb-3">
                  <strong>9.</strong> ऑनलाइन पाठ्यक्रम में प्रवेश के लिए सक्षम
                  प्राधिकारी का पूर्व अनुमोदन आवश्यक है।
                </p>
              </div>
            </div>

            {/* OK Button */}
            <div className="d-flex justify-content-end p-4 pt-0">
              <button
                type="button"
                className="btn text-white px-5 py-2 fw-bold"
                style={{
                  backgroundColor: "#B85450",
                  borderRadius: "25px",
                  fontSize: "16px",
                }}
                onClick={() => setShowOverviewModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}

      {/* Full Page Loader */}
      <FullPageLoader show={showLoadingModal} message={loadingMessage} />

      {/* Response Modal */}
      {showResponseModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 2000 }}
          />

          {/* Response Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
            style={{
              zIndex: 2001,
              width: "90%",
              maxWidth: "500px",
            }}
          >
            {/* Modal Header */}
            <div
              className={`d-flex align-items-center justify-content-between p-3 border-bottom ${responseType === "success" ? "bg-success" : "bg-info"
                }`}
            >
              <h5 className="mb-0 text-white fw-bold">
                {responseType === "success" ? (
                  <>
                    <i className="ti ti-check-circle me-2"></i>
                    Success
                  </>
                ) : (
                  <>
                    <i className="ti ti-info-circle me-2"></i>
                    Notice
                  </>
                )}
              </h5>
              <button
                className="btn btn-link text-white text-decoration-none p-0"
                onClick={() => {
                  setShowResponseModal(false);
                  if (responseType === "success") {
                    navigate(routes.noc.onlineCourse);
                  }
                }}
              >
                <i className="ti ti-x fs-20"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <div
                className={`p-3 rounded ${responseType === "success"
                  ? "bg-success bg-opacity-10"
                  : "bg-info bg-opacity-10"
                  }`}
              >
                <p className="mb-0 text-dark">{responseMessage}</p>
              </div>

              <div className="d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className={`btn ${responseType === "success" ? "btn-success" : "btn-info"
                    } px-5`}
                  onClick={() => {
                    setShowResponseModal(false);
                    if (responseType === "success") {
                      navigate(routes.noc.onlineCourse);
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NOCOnlineCourseCreate;
